import "./MobileOfferDetail.css";
import chestImage from "../../../chest.png";
import { useCallback, useEffect, useRef, useState } from "react";
import { deactivateOfferForUser, offersApi, openChest, startOfferwallOffer } from "../../../api";
import usePullToRefresh from "../../../hooks/usePullToRefresh";

const FALLBACK_TEXT =
  "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Duis mollis orci id ipsum blandit, ac mattis nisi tristique. Integer ac risus non elit blandit ullamcorper.";

const FALLBACK_SUPPORT =
  "Rewards are granted upon completion. Some advertisers may take up to 48 hours to validate.";

const FALLBACK_REWARD_CONDITIONS =
  "Complete the highlighted actions to receive your reward.";

export default function MobileOfferDetail({ offer, onBack, onRemoveActiveOffer, showBackButton = true }) {
  const [localOffer, setLocalOffer] = useState(offer);
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState("");
  const [currentDestination, setCurrentDestination] = useState("");
  const [queryUserId, setQueryUserId] = useState("");
  const [querySiteUserId, setQuerySiteUserId] = useState("");
  const [queryPackageName, setQueryPackageName] = useState("");
  const [queryApiKey, setQueryApiKey] = useState("");
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [chestCountdown, setChestCountdown] = useState(null);
  const [isOpeningChest, setIsOpeningChest] = useState(false);
  const [hasOpenedChest, setHasOpenedChest] = useState(false);
  const [showChestReward, setShowChestReward] = useState(false);
  const [chestRewardValue, setChestRewardValue] = useState(100);
  const [chestLevelsOverride, setChestLevelsOverride] = useState(null);
  const hasStartedRef = useRef(false);
  const currentOffer = localOffer || offer;

  useEffect(() => {
    setLocalOffer(offer);
  }, [offer]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const nextUserId = params.get("userId") || "";
    const nextSiteUserId = params.get("siteUserId") || "";
    const nextPackageName = params.get("packageName") || "";
    const nextApiKey = params.get("apiKey") || "";
    console.log("MobileOfferDetail query params", {
      userId: nextUserId,
      siteUserId: nextSiteUserId,
      packageName: nextPackageName,
      apiKey: nextApiKey ? "***" : "",
    });
    setQueryUserId(nextUserId);
    setQuerySiteUserId(nextSiteUserId);
    setQueryPackageName(nextPackageName);
    setQueryApiKey(nextApiKey);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const androidApp = window.Android;
    // if (androidApp?.requestUsageStatsPermission) {
    //   androidApp.requestUsageStatsPermission();
    // }
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (showChestReward) {
      document.body.classList.add("reward-modal-open");
    } else {
      document.body.classList.remove("reward-modal-open");
    }
    return () => {
      document.body.classList.remove("reward-modal-open");
    };
  }, [showChestReward]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (showRemoveConfirm) {
      document.body.classList.add("confirm-modal-open");
    } else {
      document.body.classList.remove("confirm-modal-open");
    }
    return () => {
      document.body.classList.remove("confirm-modal-open");
    };
  }, [showRemoveConfirm]);

  const refreshOfferDetails = useCallback(async () => {
    const userId = currentOffer?.userId || queryUserId || querySiteUserId;
    const packageName = currentOffer?.packageName || queryPackageName;
    if (!userId || !packageName) return;
    try {
      let token = "";
      if (typeof window !== "undefined") {
        try {
          token = window.sessionStorage.getItem("offerwallAuthToken") || "";
        } catch (err) {
          console.warn("MobileOfferDetail token read failed", err);
        }
      }
      const offersRes = queryApiKey
        ? await offersApi.getOfferwallConfigByApiKey({
          apiKey: queryApiKey,
          userId,
          siteUserId: querySiteUserId,
          packageName,
        })
        : await offersApi.getAllAppsForUser({
          userId,
          siteUserId: querySiteUserId,
          packageName,
          token: token || undefined,
        });
      const apps =
        (Array.isArray(offersRes) && offersRes) ||
        offersRes?.result?.apps ||
        offersRes?.apps ||
        [];
      const activeApps =
        offersRes?.result?.activeApps ||
        offersRes?.activeApps ||
        [];
      const normalize = (app) => {
        if (!app) return null;
        return {
          ...app,
          id: app.packageName || app.appName || app.appId || app.id,
          title: app.displayName || app.appName || app.name || app.title,
          packageName: app.packageName || app.appId || app.id,
          icon: app.logo,
          reward: app.rewardTitle ?? app.reward,
          description: app.offerLongDesc || app.description,
          rewardConditions: app.rewardConditions,
          support: app.supportText || app.support,
          tagline: app.offerShortDesc || app.tagline,
        };
      };
      const normalizedApps = apps.map(normalize).filter(Boolean);
      const normalizedActive = activeApps
        .map(normalize)
        .filter(Boolean);
      const buildKeys = (item) =>
        [
          item?.appName,
          item?.displayName,
          item?.title,
          item?.name,
          item?.packageName,
          item?.appId,
          item?.id,
        ]
          .filter(Boolean)
          .map((value) => String(value).trim().toLowerCase());
      const targetKeys = new Set(buildKeys(currentOffer));
      const matchFromApps = normalizedApps.find((item) => buildKeys(item).some((key) => targetKeys.has(key)));
      const matchFromActive = normalizedActive.find((item) => buildKeys(item).some((key) => targetKeys.has(key)));
      const mergedMatch = matchFromApps
        ? { ...matchFromActive, ...matchFromApps }
        : matchFromActive;
      if (mergedMatch) {
        const preferExistingNumber = (prevValue, nextValue) => {
          const prevNum = Number(prevValue);
          const nextNum = Number(nextValue);
          if (Number.isFinite(prevNum) && prevNum > 0 && (!Number.isFinite(nextNum) || nextNum === 0)) {
            return prevNum;
          }
          return nextValue;
        };
        const prevOffer = currentOffer || {};
        const nextOffer = {
          ...prevOffer,
          ...mergedMatch,
          registrationCoins: preferExistingNumber(prevOffer.registrationCoins, mergedMatch.registrationCoins),
          minuteIncrement: preferExistingNumber(prevOffer.minuteIncrement, mergedMatch.minuteIncrement),
          minuteCoinsTotal: preferExistingNumber(prevOffer.minuteCoinsTotal, mergedMatch.minuteCoinsTotal),
          chestCoinsTotal: preferExistingNumber(prevOffer.chestCoinsTotal, mergedMatch.chestCoinsTotal),
          chestStats: {
            ...(prevOffer.chestStats || {}),
            ...(mergedMatch.chestStats || {}),
            totalRewards: preferExistingNumber(
              prevOffer.chestStats?.totalRewards,
              mergedMatch.chestStats?.totalRewards
            ),
            totalChests: preferExistingNumber(
              prevOffer.chestStats?.totalChests,
              mergedMatch.chestStats?.totalChests
            ),
          },
          rewardMeta: {
            ...(prevOffer.rewardMeta || {}),
            ...(mergedMatch.rewardMeta || {}),
            registration: {
              ...(prevOffer.rewardMeta?.registration || {}),
              ...(mergedMatch.rewardMeta?.registration || {}),
              amount: preferExistingNumber(
                prevOffer.rewardMeta?.registration?.amount,
                mergedMatch.rewardMeta?.registration?.amount
              ),
            },
            minute: {
              ...(prevOffer.rewardMeta?.minute || {}),
              ...(mergedMatch.rewardMeta?.minute || {}),
              base: preferExistingNumber(
                prevOffer.rewardMeta?.minute?.base,
                mergedMatch.rewardMeta?.minute?.base
              ),
              increment: preferExistingNumber(
                prevOffer.rewardMeta?.minute?.increment,
                mergedMatch.rewardMeta?.minute?.increment
              ),
            },
          },
        };
        setLocalOffer((prev) => ({ ...(prev || prevOffer), ...nextOffer }));
        if (typeof window !== "undefined") {
          window.sessionStorage.setItem("mobileSelectedOffer", JSON.stringify(nextOffer));
        }
      }
    } catch (err) {
      console.warn("MobileOfferDetail refresh offer failed", err);
    }
  }, [currentOffer, queryUserId, querySiteUserId, queryPackageName, queryApiKey]);

  usePullToRefresh({ onRefresh: refreshOfferDetails });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const notifyAppInForeground = () => {
      refreshOfferDetails();
    };
    window.notifyAppInForeground = notifyAppInForeground;
    return () => {
      if (window.notifyAppInForeground === notifyAppInForeground) {
        delete window.notifyAppInForeground;
      }
    };
  }, []);

  useEffect(() => {
    if (!currentOffer || typeof window === "undefined") return;
    if (!queryUserId || !queryPackageName) return;
    let skipStart = false;
    try {
      const ptrRefreshAt = Number(window.sessionStorage.getItem("ptrRefreshAt"));
      if (Number.isFinite(ptrRefreshAt) && Date.now() - ptrRefreshAt < 30000) {
        window.sessionStorage.removeItem("ptrRefreshAt");
        skipStart = true;
      }
    } catch (err) {
      // ignore storage errors
    }
    if (skipStart) {
      refreshOfferDetails();
      return;
    }
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;
    let canceled = false;
    const startOffer = async () => {
      try {
        console.log("MobileOfferDetail startOfferwallOffer request", {
          userId: currentOffer.userId || queryUserId,
          siteUserId: querySiteUserId,
          packageName: currentOffer.packageName || queryPackageName,
        });
        const response = await startOfferwallOffer({
          userId: currentOffer.userId || queryUserId,
          siteUserId: querySiteUserId,
          packageName: currentOffer.packageName || queryPackageName,
        });
        console.log("MobileOfferDetail startOfferwallOffer response", response);
        if (canceled) return;
        const destination = typeof response?.result === "string"
          ? response.result
          : response?.result;
        if (destination) {
          const androidApp = window.Android;
          if (androidApp?.setDeepLinkHost) {
            androidApp.setDeepLinkHost(destination);
          }
          setCurrentDestination(destination);
        } else {
          console.warn("MobileOfferDetail missing destination in response", response);
        }
      } catch (err) {
        console.warn("MobileOfferDetail startOfferwallOffer failed", err);
      }
    };
    startOffer();
    return () => { canceled = true; };
  }, [currentOffer, queryUserId, querySiteUserId, queryPackageName]);

  const handlePlayNow = async () => {
    if (isStarting) return;
    setStartError("");
    setIsStarting(true);
    try {
      // const response = await startOfferwallOffer({
      //   userId: offer.userId || queryUserId,
      //   siteUserId: querySiteUserId,
      //   packageName: offer.packageName || queryPackageName,
      // });
      // const destination = typeof response?.result === "string"
      //   ? response.result
      //   : response?.result;
      // if (destination) {
      //   const androidApp = window.Android;
      //   if (androidApp?.setDeepLinkHost) {
      //     androidApp.setDeepLinkHost(destination);
      //   } else if (typeof window.open === "function") {
      //     window.open(destination, "_blank", "noopener,noreferrer");
      //   }

      if (typeof window !== "undefined") {
        const openUrl =
          currentOffer?.openUrl ||
          currentOffer?.app?.openUrl ||
          currentOffer?.details?.openUrl ||
          "";
        const targetUrl = openUrl || currentDestination;
        console.log("MobileOfferDetail Play Now", { targetUrl });
        if (targetUrl) {
          window.open(targetUrl, "_blank", "noopener,noreferrer");
        } else {
          setStartError("Неуспешно получаване на адрес за стартиране.");
        }
      }
      return;
      // }
      // throw new Error("Неуспешно получаване на адрес за стартиране.");
    } catch (err) {
      setStartError(err?.message || "Неуспешно стартиране на офертата.");
    } finally {
      setIsStarting(false);
    }
  };

  const handleRemoveOffer = async () => {
    if (!currentOffer || !isActiveOffer) return;
    setShowRemoveConfirm(false);
    const userId = currentOffer.userId || queryUserId || querySiteUserId;
    const appName = currentOffer.appName || currentOffer.app?.appName || currentOffer.name || currentOffer.title || currentOffer.packageName;
    if (!userId || !appName) {
      console.warn("MobileOfferDetail deactivateOfferForUser missing fields", { userId, appName });
      return;
    }
    try {
      await deactivateOfferForUser({
        userId,
        siteUserId: querySiteUserId,
        appName,
      });
    } catch (err) {
      console.warn("MobileOfferDetail deactivateOfferForUser failed", err);
    }
    onRemoveActiveOffer?.(currentOffer);
  };

  const {
    title,
    displayName,
    appName,
    icon,
    logo,
    media = icon || logo,
    reward,
    rewardTitle,
    description,
    offerLongDesc,
    rewardConditions,
    support,
    supportText,
    level,
    rewardMeta,
    rewards,
  } = currentOffer || {};

  const resolvedTitle = displayName || title || appName || "Offer title";
  const resolvedReward = rewardTitle || reward || "10M coins";
  const rewardPillText = resolvedReward || "10M coins";
  const resolvedDescription = offerLongDesc || description || FALLBACK_TEXT;
  const resolvedRewardConditions = rewardConditions || FALLBACK_REWARD_CONDITIONS;
  const resolvedSupport = supportText || support || FALLBACK_SUPPORT;
  const heroImage = media || icon || logo || "/default-offer.png";
  const rewardConditionLines = resolvedRewardConditions
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const numberedConditions = rewardConditionLines.filter((line) => /^\d+\./.test(line));
  const rewardSteps = numberedConditions.length ? numberedConditions : rewardConditionLines;
  const supportUrlMatches = resolvedSupport?.match(/https?:\/\/\S+/g) || [];
  const supportUrls = supportUrlMatches
    .map((url) => url.replace(/[).,!?]+$/, ""))
    .filter(Boolean);
  const supportBodyText = supportUrls.length
    ? supportUrls.reduce((text, url) => text.replace(url, ""), resolvedSupport).trim()
    : resolvedSupport;
  const supportQuery = typeof window !== "undefined" ? window.location.search : "";
  const supportLink = `https://givvyoffers.com/contact${supportQuery || ""}`;
  const supportDisplay = "https://givvyoffers.com/contact";

  const toNumber = (value) => {
    if (value === null || value === undefined || value === "") return NaN;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : NaN;
  };
  const readOfferNumber = (key) =>
    toNumber(currentOffer?.[key]) ??
    toNumber(currentOffer?.app?.[key]) ??
    toNumber(currentOffer?.details?.[key]);
  const rewardMetaResolved = rewardMeta || currentOffer?.app?.rewardMeta || currentOffer?.details?.rewardMeta;
  const rewardsList = Array.isArray(rewards)
    ? rewards
    : Array.isArray(currentOffer?.app?.rewards)
      ? currentOffer.app.rewards
      : Array.isArray(currentOffer?.details?.rewards)
        ? currentOffer.details.rewards
        : [];
  const registrationRewardItem = rewardsList.find((item) => {
    if (!item) return false;
    const action = String(item.action || "").toUpperCase();
    const rewardType = String(item.rewardType || "").toLowerCase();
    const name = String(item.name || item.label || "").toLowerCase();
    return action === "CPI" || rewardType === "singlereward" || name.includes("register");
  });
  const registrationAmountFromApi = Number(registrationRewardItem?.amount);
  const registrationUsdText = Number.isFinite(registrationAmountFromApi)
    ? `$${registrationAmountFromApi}`
    : "$0.01";
  const registrationCoinsRaw = toNumber(rewardMetaResolved?.registration?.amount);
  const minuteBaseRaw = toNumber(rewardMetaResolved?.minute?.base);
  const minuteIncrementRaw = toNumber(rewardMetaResolved?.minute?.increment);
  const minuteCoinsTotalRaw = readOfferNumber("minuteCoinsTotal");
  let baseRegistrationCoins = registrationCoinsRaw;
  let minuteIncrement = minuteIncrementRaw;
  let minuteBase = minuteBaseRaw;

  if (!Number.isFinite(baseRegistrationCoins)) {
    baseRegistrationCoins = readOfferNumber("registrationCoins");
  }
  if (!Number.isFinite(minuteIncrement)) {
    minuteIncrement = readOfferNumber("minuteIncrement");
  }
  if (!Number.isFinite(minuteBase)) {
    minuteBase = baseRegistrationCoins;
  }
  if (!Number.isFinite(baseRegistrationCoins) && Number.isFinite(minuteCoinsTotalRaw) && Number.isFinite(minuteIncrement)) {
    baseRegistrationCoins = minuteCoinsTotalRaw - minuteIncrement;
  }
  if (!Number.isFinite(minuteIncrement) && Number.isFinite(minuteCoinsTotalRaw) && Number.isFinite(baseRegistrationCoins)) {
    minuteIncrement = minuteCoinsTotalRaw - baseRegistrationCoins;
  }
  if (!Number.isFinite(baseRegistrationCoins)) baseRegistrationCoins = 0;
  if (!Number.isFinite(minuteIncrement)) minuteIncrement = 0;
  if (!Number.isFinite(minuteBase)) minuteBase = baseRegistrationCoins;
  const isActiveOffer = Number.isFinite(Number(level)) || currentOffer?.isActiveOffer;
  const currentLevel = Math.max(0, Number(level) || 0);
  const minutesCompleted = Math.max(0, currentLevel - 1);
  const coinsFromMinutes = minutesCompleted * minuteIncrement;
  const chestStatsTotalRewards = Number(currentOffer?.chestStats?.totalRewards);
  const coinsFromChests = Number.isFinite(chestStatsTotalRewards)
    ? chestStatsTotalRewards
    : Number(currentOffer?.chestCoinsTotal ?? 0) || 0;
  const explicitTotalCoins = Number(currentOffer?.totalCoins ?? currentOffer?.coinsEarned);
  const coinsEarned = Number.isFinite(explicitTotalCoins)
    ? explicitTotalCoins
    : baseRegistrationCoins + coinsFromMinutes + coinsFromChests;
  const nextMinuteIndex = Math.max(1, currentLevel);
  const nextMinuteCoins = minuteBase + minuteIncrement * nextMinuteIndex;
  const chestInterval = 30;
  const chestStatsTotalChests = Number(currentOffer?.chestStats?.totalChests);
  const chestsOpened = Number.isFinite(chestStatsTotalChests)
    ? chestStatsTotalChests
    : Number(currentOffer?.chestsOpened ?? Math.floor(minutesCompleted / chestInterval)) || 0;
  const chestMeta = currentOffer?.chest;
  const chestStatus = chestMeta?.status || "";
  const chestRemainingLevelsRaw = chestMeta?.remainingLevels;
  const chestRemainingLevels = Number.isFinite(Number(chestRemainingLevelsRaw))
    ? Math.max(0, Number(chestRemainingLevelsRaw))
    : null;
  const effectiveRemainingLevels = chestLevelsOverride ?? chestRemainingLevels;
  const isChestReadyFromOffer = chestStatus.toLowerCase() === "ready" || chestRemainingLevels === 0;
  const isChestReady = (isChestReadyFromOffer || chestCountdown === 0) && !hasOpenedChest;

  useEffect(() => {
    setHasOpenedChest(false);
    setChestLevelsOverride(null);
  }, [currentOffer?.id, currentOffer?.packageName, chestMeta?.offerConvertId, chestMeta?.remainingLevels, minutesCompleted]);

  useEffect(() => {
    if (!isActiveOffer) return;
    const levelsLeft = effectiveRemainingLevels ?? 0;
    setChestCountdown(levelsLeft);
  }, [isActiveOffer, effectiveRemainingLevels]);

  const handleOpenChest = async () => {
    if (!currentOffer || !isChestReady || isOpeningChest) return;
    const userId = currentOffer.userId || queryUserId || querySiteUserId;
    const packageName = currentOffer.packageName || queryPackageName;
    const appName = chestMeta?.appName || currentOffer.appName || currentOffer.app?.appName || currentOffer.name || currentOffer.title || currentOffer.packageName;
    const offerConvertId = chestMeta?.offerConvertId;
    const appId = chestMeta?.appId;
    if (!userId || !packageName) {
      console.warn("MobileOfferDetail openChest missing fields", { userId, packageName });
      return;
    }
    setIsOpeningChest(true);
    try {
      const response = await openChest({
        userId,
        siteUserId: querySiteUserId,
        packageName,
        appName,
        offerConvertId,
        appId,
      });
      console.log("MobileOfferDetail openChest response", response);
      const result = response?.result || response;
      const rewardValue = Number(result?.reward);
      if (Number.isFinite(rewardValue)) setChestRewardValue(rewardValue);
      const remainingLevels = Number(result?.remainingLevels);
      if (Number.isFinite(remainingLevels)) {
        setChestLevelsOverride(Math.max(0, remainingLevels));
      }
      await refreshOfferDetails();
      setHasOpenedChest(true);
      setShowChestReward(true);
    } catch (err) {
      console.warn("MobileOfferDetail openChest failed", err);
    } finally {
      setIsOpeningChest(false);
    }
  };

  const formatCountdown = (levels) => {
    if (levels === null || Number.isNaN(levels)) return "";
    const safeLevels = Math.max(0, Math.ceil(levels));
    return `${safeLevels} mins left`;
  };

  const chestLabel = isOpeningChest
    ? "Opening..."
    : hasOpenedChest
      ? "Chest opened"
      : isChestReady
        ? "Open chest"
        : formatCountdown(chestCountdown);

  if (!currentOffer) return null;

  return (
    <div className="m-offer-detail">
      <div className="m-offer-detail-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        {showBackButton && (
          <button className="m-offer-detail-back" onClick={onBack} aria-label="Back">
            ➜
          </button>
        )}
      </div>

      <div className="m-offer-detail-content">
        <div className="m-offer-detail-head">
          <div className="m-offer-detail-title">{resolvedTitle}</div>
          <span className="m-offer-detail-pill">
            <span className="m-offer-detail-pill-inner">{rewardPillText}</span>
          </span>
        </div>
        {isActiveOffer && (
          <div className="m-offer-detail-active">
            <div className="m-offer-detail-active-bar">
              <span className="m-offer-detail-active-pill">Active</span>
              <div className="m-offer-detail-total-pill">
                <span>Total coins</span>
                <strong>
                  {coinsEarned}
                  <span className="coin-icon" aria-hidden="true" />
                </strong>
              </div>
            </div>
            <div className="m-offer-detail-active-stats">
              <div className="m-offer-detail-active-stat">
                <span>Minutes spent</span>
                <strong>{minutesCompleted}</strong>
              </div>
              <div className="m-offer-detail-active-stat">
                <span>Chests opened</span>
                <strong>{chestsOpened}</strong>
              </div>
            </div>
            <div className="m-offer-detail-active-cards">
              <div className="m-offer-detail-active-card">
                <span className="m-offer-detail-active-card-icon">🧾</span>
                <span>Registration</span>
                <strong>
                  {baseRegistrationCoins}
                  <span className="coin-icon" aria-hidden="true" />
                </strong>
              </div>
              <div className="m-offer-detail-active-card">
                <span className="m-offer-detail-active-card-icon">⏱️</span>
                <span>Minutes</span>
                <strong>
                  {coinsFromMinutes}
                  <span className="coin-icon" aria-hidden="true" />
                </strong>
              </div>
              <div className="m-offer-detail-active-card">
                <span className="m-offer-detail-active-card-icon">🎁</span>
                <span>Chests</span>
                <strong>
                  {coinsFromChests}
                  <span className="coin-icon" aria-hidden="true" />
                </strong>
              </div>
            </div>
          </div>
        )}

        {isActiveOffer && (
          <section className="m-offer-detail-section">
            <div className="m-offer-detail-section-head">
              <h4>NEXT STEPS</h4>
              <span>3 items</span>
            </div>
            <div className="m-offer-detail-next">
              <div className="m-offer-detail-next-item is-complete">
                <span className="m-offer-detail-next-icon">✅</span>
                <div>
                  <span>Registration</span>
                  <strong>Done</strong>
                </div>
              </div>
              <div className="m-offer-detail-next-item">
                <span className="m-offer-detail-next-icon">⏱️</span>
                <div>
                  <span className="m-offer-detail-next-label">
                    <span>Next minute</span>
                    <span>reward</span>
                  </span>
                  <strong>
                    <span className="coin-icon" aria-hidden="true" />
                    {`+${minuteIncrement}`}
                  </strong>
                </div>
              </div>
              <button
                className={`m-offer-detail-next-item is-action${isChestReady ? " is-ready" : ""}`}
                type="button"
                disabled={!isChestReady || isOpeningChest}
                onClick={handleOpenChest}
              >
                <span className="m-offer-detail-next-icon">🎁</span>
                <div>
                  <span>Chest reward</span>
                  <strong>
                    {chestLabel}
                  </strong>
                </div>
              </button>
            </div>
          </section>
        )}

        <section className="m-offer-detail-section">
          <h4>DESCRIPTION</h4>
          <p>{resolvedDescription}</p>
        </section>

        <section className="m-offer-detail-section">
          <h4>REWARD CONDITIONS</h4>
          {rewardSteps.length > 1 ? (
            <ol className="m-offer-detail-steps">
              {rewardSteps.map((step, index) => (
                <li key={index}>{step.replace(/^\d+\.\s*/, "")}</li>
              ))}
            </ol>
          ) : (
            <p>{resolvedRewardConditions}</p>
          )}
        </section>

        <section className="m-offer-detail-section">
          <div className="m-offer-detail-section-head">
            <h4>REWARDS</h4>
            <span>3 steps</span>
          </div>
          <div className="m-offer-reward-steps">
            <div className="m-offer-reward-step">
              <div className="m-offer-reward-step-icon">🧾</div>
              <div className="m-offer-reward-step-body">
                <div className="m-offer-reward-step-title">Registration bonus</div>
                <div className="m-offer-reward-step-value">
                  {`Earn ${registrationUsdText}`}
                </div>
                <div className="m-offer-reward-step-subtitle">One-time reward after signup</div>
              </div>
            </div>
            <div className="m-offer-reward-step">
              <div className="m-offer-reward-step-icon">⏱️</div>
              <div className="m-offer-reward-step-body">
                <div className="m-offer-reward-step-title">Every minute</div>
                <div className="m-offer-reward-step-value">
                  Earn $0.002 - $0.02
                </div>
                <div className="m-offer-reward-step-subtitle">For each minute in session</div>
              </div>
            </div>
            <div className="m-offer-reward-step is-highlight">
              <div className="m-offer-reward-step-icon">🎁</div>
              <div className="m-offer-reward-step-body">
                <div className="m-offer-reward-step-title">Mystery chest</div>
                <div className="m-offer-reward-step-value">
                  Earn $0.001 - $5
                </div>
                <div className="m-offer-reward-step-subtitle">Random reward every 30 minutes in session</div>
              </div>
            </div>
          </div>
        </section>

        <section className="m-offer-detail-section">
          <h4>SUPPORT</h4>
          <p>
            {supportBodyText}
            {supportLink && (
              <>
                {" "}
                <a href={supportLink} target="_blank" rel="noopener noreferrer">
                  {supportDisplay}
                </a>
              </>
            )}
          </p>
        </section>

        <button
          className="m-offer-detail-cta"
          type="button"
          disabled={isStarting}
          aria-busy={isStarting ? "true" : "false"}
          onClick={handlePlayNow}
        >
          {isStarting ? "Starting…" : "Play Now"}
        </button>
        {isActiveOffer && (
          <button
            className="m-offer-detail-cta is-secondary"
            type="button"
            onClick={() => setShowRemoveConfirm(true)}
          >
            Remove Offer
          </button>
        )}
        {startError && (
          <p className="m-offer-detail-error" role="alert">
            {startError}
          </p>
        )}
      </div>
      {showRemoveConfirm && (
        <div className="m-offer-detail-modal" role="dialog" aria-modal="true">
          <div className="m-offer-detail-modal-card">
            <div className="m-offer-detail-modal-title">Remove active offer?</div>
            <p>This will remove the offer from your active list.</p>
            <div className="m-offer-detail-modal-actions">
              <button
                className="m-offer-detail-modal-btn is-secondary"
                type="button"
                onClick={() => setShowRemoveConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="m-offer-detail-modal-btn is-danger"
                type="button"
                onClick={handleRemoveOffer}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
      {showChestReward && (
        <div className="m-offer-detail-modal is-reward" role="dialog" aria-modal="true">
          <div className="m-offer-detail-modal-card is-reward">
            <div className="m-offer-detail-reward-burst" aria-hidden="true" />
            <div className="m-offer-detail-reward-icon" aria-hidden="true">
              <img className="m-offer-detail-reward-image" src={chestImage} alt="" />
            </div>
            <div className="m-offer-detail-modal-title">Chest opened!</div>
            <div className="m-offer-detail-reward-amount">
              +{chestRewardValue}
              <span className="coin-icon" aria-hidden="true" />
            </div>
            <p className="m-offer-detail-reward-subtitle">Bonus coins added to your balance.</p>
            <button
              className="m-offer-detail-modal-btn is-reward"
              type="button"
              onClick={() => {
                setShowChestReward(false);
              }}
            >
              Awesome
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
